import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import Img from '../../../../Img';
import PopupV3 from '../../../../Popup/PopupV3';
import MobileModal from '../../../../../pages/SearchPage/SearchV2/SharedComponents/MobileModal';
import useIsMobile from '../../../../hooks/useIsMobile';
import { profilePossessive } from '../performanceInsight';
import { capitalize } from '../../../../../utils/formatting';
import styles from '../performanceStats.scss';
import { IMPACT_DOT_DIAMETERS } from '../performanceStatsUtils';

export function InfoTrigger({ onOpen, iconClassName, 'data-testid': dataTestId }) {
    return (
        <button type="button" className={styles.infoIconTrigger} onClick={onOpen} data-testid={dataTestId}>
            <Img src="/img/info-icon.svg" alt="info" className={iconClassName || styles.infoIcon} data-testid={dataTestId && `${dataTestId}.icon`} />
        </button>
    );
}

InfoTrigger.propTypes = {
    onOpen: PropTypes.func.isRequired,
    iconClassName: PropTypes.string,
    'data-testid': PropTypes.string,
};

InfoTrigger.defaultProps = {
    iconClassName: null,
    'data-testid': null,
};

export function InfoPopup({ isOpen, onClose, title, children, 'data-testid': dataTestId }) {
    const { isMobileWidth } = useIsMobile();

    if (isMobileWidth) {
        return (
            <MobileModal
                isOpen={isOpen}
                onClose={onClose}
                title={title}
                titleClassName={styles.infoModalTitle}
                showFooter={false}
                data-testid={dataTestId}
            >
                <div className={styles.infoContent} data-testid={dataTestId && `${dataTestId}.content`}>{children}</div>
            </MobileModal>
        );
    }

    return (
        <PopupV3
            isOpen={isOpen}
            onCancelClick={onClose}
            showConfirmButton={false}
            showCancelButton={false}
            message={(
                <React.Fragment>
                    <div className={styles.desktopInfoPopupTitle} data-testid={dataTestId && `${dataTestId}.title`}>{title}</div>
                    <div className={styles.infoContent} data-testid={dataTestId && `${dataTestId}.content`}>{children}</div>
                    <div className={styles.desktopInfoPopupFooter}>
                        <button
                            type="button"
                            className={styles.desktopInfoCloseBtn}
                            onClick={onClose}
                            data-testid={dataTestId && `${dataTestId}.close-btn`}
                        >
                            CLOSE
                        </button>
                    </div>
                </React.Fragment>
            )}
        />
    );
}

InfoPopup.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    'data-testid': PropTypes.string,
};

InfoPopup.defaultProps = {
    'data-testid': null,
};

export function ImpactInfo({ isOwnProfile, classes, 'data-testid': dataTestId }) {
    const [isOpen, setIsOpen] = useState(false);
    const possessive = profilePossessive(isOwnProfile);
    const infoTestId = dataTestId && `${dataTestId}.info`;
    return (
        <React.Fragment>
            <InfoTrigger
                onOpen={() => setIsOpen(true)}
                iconClassName={classes.impactInfoIcon}
                data-testid={dataTestId && `${dataTestId}.info-btn`}
            />
            <InfoPopup
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={`Impact on ${capitalize(possessive)} Rating.`}
                data-testid={infoTestId}
            >
                <p data-testid={infoTestId && `${infoTestId}.intro`}>Impact on {possessive} rating is based on:</p>
                <ul data-testid={infoTestId && `${infoTestId}.factors`}>
                    <li data-testid={infoTestId && `${infoTestId}.factors.opponent-reliability`}>Opponent reliability</li>
                    <li data-testid={infoTestId && `${infoTestId}.factors.match-recency`}>Match recency</li>
                    <li data-testid={infoTestId && `${infoTestId}.factors.games-played`}>Number of games played</li>
                </ul>
            </InfoPopup>
        </React.Fragment>
    );
}

ImpactInfo.propTypes = {
    isOwnProfile: PropTypes.bool,
    classes: PropTypes.objectOf(PropTypes.string),
    'data-testid': PropTypes.string,
};

ImpactInfo.defaultProps = {
    isOwnProfile: true,
    classes: styles,
    'data-testid': null,
};

export const LegendSheet = ({ isOpen, onClose, children, inline, 'data-testid': dataTestId }) => {
    useEffect(() => {
        if (!isOpen) { return undefined; }
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') { onClose(); }
        };
        if (!inline) {
            document.body.style.overflow = 'hidden';
        }
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            if (!inline) {
                document.body.style.overflow = 'unset';
            }
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose, inline]);

    if (!isOpen || typeof document === 'undefined') { return null; }

    const overlayClassName = inline
        ? `${styles.legendSheetOverlay} ${styles.legendSheetOverlayInline}`
        : styles.legendSheetOverlay;
    const sheetClassName = inline
        ? `${styles.legendSheet} ${styles.legendSheetInline}`
        : styles.legendSheet;

    const content = (
        <React.Fragment>
            <div className={overlayClassName} onClick={onClose} data-testid={dataTestId && `${dataTestId}.overlay`} />
            <div className={sheetClassName} data-testid={dataTestId}>
                <button
                    type="button"
                    className={styles.legendSheetCloseBtn}
                    onClick={onClose}
                    data-testid={dataTestId && `${dataTestId}.close-btn`}
                >
                    <Img src="/img/icons/close-icon-lg.svg" alt="Close" className={styles.legendSheetCloseIcon} data-testid={dataTestId && `${dataTestId}.close-btn.icon`} />
                </button>
                {children}
            </div>
        </React.Fragment>
    );

    if (inline) {
        return content;
    }

    return ReactDOM.createPortal(content, document.body);
};

LegendSheet.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    inline: PropTypes.bool,
    'data-testid': PropTypes.string,
};

LegendSheet.defaultProps = {
    inline: false,
    'data-testid': null,
};

export function ScatterLegendList({ horizontal, 'data-testid': dataTestId }) {
    const listClassName = horizontal
        ? `${styles.legendSheetList} ${styles.legendSheetListHorizontal}`
        : styles.legendSheetList;
    return (
        <ul className={listClassName} data-testid={dataTestId && `${dataTestId}.list`}>
            <li className={styles.legendSheetItem} data-testid={dataTestId && `${dataTestId}.consistent`}>
                <span className={styles.legendSheetSwatch}>
                    <span className={`${styles.legendSheetDot} ${styles.legendSheetDotConsistent}`} />
                </span>
                {'Consistent'}
            </li>
            <li className={styles.legendSheetItem} data-testid={dataTestId && `${dataTestId}.above`}>
                <span className={styles.legendSheetSwatch}>
                    <span className={`${styles.legendSheetDot} ${styles.legendSheetDotAbove}`} />
                </span>
                {'Above'}
            </li>
            <li className={styles.legendSheetItem} data-testid={dataTestId && `${dataTestId}.below`}>
                <span className={styles.legendSheetSwatch}>
                    <span className={`${styles.legendSheetDot} ${styles.legendSheetDotBelow}`} />
                </span>
                {'Below'}
            </li>
            <li className={styles.legendSheetItem} data-testid={dataTestId && `${dataTestId}.band`}>
                <span className={styles.legendSheetSwatch}>
                    <span className={styles.legendSheetBand} />
                </span>
                {'Consistency Band'}
            </li>
            <li className={styles.legendSheetItem} data-testid={dataTestId && `${dataTestId}.impact`}>
                <span className={styles.legendSheetSwatch}>
                    {IMPACT_DOT_DIAMETERS.map(diameter => (
                        <span
                            key={`legend-impact-${diameter}`}
                            className={`${styles.legendSheetDot} ${styles.legendSheetDotImpact}`}
                            style={{ width: `${diameter}px`, height: `${diameter}px` }}
                        />
                    ))}
                </span>
                {'Impact on your UTR'}
            </li>
        </ul>
    );
}

ScatterLegendList.propTypes = {
    horizontal: PropTypes.bool,
    'data-testid': PropTypes.string,
};

ScatterLegendList.defaultProps = {
    horizontal: false,
    'data-testid': null,
};

export function ConsistencyInfoBody({ 'data-testid': dataTestId }) {
    return (
        <p data-testid={dataTestId && `${dataTestId}.intro`}>
            Performance Consistency measures how much your performance varies from match to match.
            The blue band shows your typical performance range. A lower Performance Consistency score
            means you perform more consistently. A higher score means your performance varies more.
        </p>
    );
}

ConsistencyInfoBody.propTypes = {
    'data-testid': PropTypes.string,
};

ConsistencyInfoBody.defaultProps = {
    'data-testid': null,
};
